import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../../services/auth';
import { userService } from '../../../../services/userService'; // Still needed for validation
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useUsers } from '../../hooks/useUsers.js';
import AppLayout from '../../../../components/layout/AppLayout/AppLayout.jsx';
import Modal from '../../../../components/ui/Modal/Modal.jsx';
import UserTable from '../../components/UserTable.jsx';
import './UserManagement.css';

const UserManagement = React.memo(({ onLogout }) => {
  const { t } = useTranslation(['user', 'common']);
  const { checkAndSyncUserLanguage } = useLanguage();
  
  // Use optimized users hook
  const usersHook = useUsers({
    limitOptions: [5, 10, 25, 50] // Custom pagination limits for users
  });
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    language: 'en'
  });
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Reset form data
  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      language: 'en'
    });
    setFormErrors({});
  };

  // Stable callback functions for UserTable
  const handleAddUser = useCallback(() => {
    resetForm();
    setShowCreateForm(true);
    setEditingUser(null);
  }, []);

  const handleEditUser = useCallback((user) => {
    setFormData({
      username: user.username,
      email: user.email,
      password: '', // Don't populate password for security
      role: user.role,
      language: user.language || 'en'
    });
    setFormErrors({});
    setEditingUser(user);
    setShowCreateForm(true);
  }, []);

  const handleFormClose = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormErrors({});
    usersHook.setError('');

    try {
      // Validate form data
      const validation = userService.validateUserData(formData, !!editingUser);
      if (!validation.isValid) {
        setFormErrors(validation.errors);
        setFormLoading(false);
        return;
      }

      let result;
      if (editingUser) {
        // Update existing user
        const updateData = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          language: formData.language
        };
        // Only include password if it's provided
        if (formData.password.trim()) {
          updateData.password = formData.password;
        }
        
        result = await usersHook.updateUser(editingUser.id, updateData);
      } else {
        // Create new user
        result = await usersHook.createUser(formData);
      }

      if (result.success) {
        // Check if we just updated the current user's language
        const currentUser = authService.getCurrentUser();
        if (editingUser && currentUser && editingUser.id === currentUser.id) {
          // If we updated the current user, sync their language immediately
          await checkAndSyncUserLanguage();
        }
        
        // Close the form
        handleFormClose();
        // Clear any errors
        usersHook.setError('');
      } else {
        // Error will be displayed in AppLayout error banner
        usersHook.setError(result.error);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      usersHook.setError('An unexpected error occurred. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };


  // Show loading state only for initial load
  if (usersHook.loading && (!usersHook.data || usersHook.data.length === 0)) {
    return (
      <AppLayout 
        title={t('user:management.title')}
        error={usersHook.error}
        setError={usersHook.setError}
        onLogout={onLogout}
      >
        <div className="user-management-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('user:management.loading_users')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout 
      title={t('user:management.title')}
      error={usersHook.error}
      setError={usersHook.setError}
      onLogout={onLogout}
    >
      {/* Optimized UserTable handles all table logic */}
      <UserTable
        hookData={usersHook}
        onAddUser={handleAddUser}
        onEditUser={handleEditUser}
      />

      <Modal
        isOpen={showCreateForm}
        onClose={handleFormClose}
        title={editingUser ? t('user:forms.edit_modal_title') : t('user:forms.create_modal_title')}
        footer={
          <>
            <button 
              type="button" 
              onClick={handleFormClose} 
              className="btn btn-secondary"
              disabled={formLoading}
            >
              {t('common:buttons.cancel')}
            </button>
            <button 
              type="submit" 
              form="user-form"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <span className="spinner-small"></span>
                  {editingUser ? t('user:forms.updating_user') : t('user:forms.creating_user')}
                </>
              ) : (
                editingUser ? t('user:forms.update_user') : t('user:forms.create_user')
              )}
            </button>
          </>
        }
      >
        <form id="user-form" onSubmit={handleFormSubmit}>
                <div className="form-group">
                  <label className="form-label">{t('user:forms.username_label')}</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleFormChange}
                    placeholder={t('user:forms.username_placeholder')}
                    className={`form-input ${formErrors.username ? 'error' : ''}`}
                    required
                  />
                  {formErrors.username && (
                    <span className="field-error">{formErrors.username}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">{t('user:forms.email_label')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder={t('user:forms.email_placeholder')}
                    className={`form-input ${formErrors.email ? 'error' : ''}`}
                    required
                  />
                  {formErrors.email && (
                    <span className="field-error">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">{t('user:forms.role_label')}</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className={`form-input ${formErrors.role ? 'error' : ''}`}
                    required
                  >
                    <option value="">{t('user:forms.select_role')}</option>
                    <option value="user">{t('common:roles.user')}</option>
                    <option value="admin">{t('common:roles.admin')}</option>
                  </select>
                  {formErrors.role && (
                    <span className="field-error">{formErrors.role}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">{t('common:labels.language')}</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleFormChange}
                    className={`form-input ${formErrors.language ? 'error' : ''}`}
                    required
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                  {formErrors.language && (
                    <span className="field-error">{formErrors.language}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('user:forms.password_label')}
                    {editingUser && <span className="optional-label"> ({t('user:forms.password_optional')})</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    placeholder={editingUser ? t('user:forms.password_placeholder_optional') : t('user:forms.password_placeholder')}
                    className={`form-input ${formErrors.password ? 'error' : ''}`}
                    required={!editingUser}
                  />
                  {formErrors.password && (
                    <span className="field-error">{formErrors.password}</span>
                  )}
                </div>
        </form>
      </Modal>

      {/* Loading overlay for actions */}
      {usersHook.actionLoading && (
        <div className="action-loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </AppLayout>
  );
});

export default UserManagement;